import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { api, errText } from "@/lib/api";
import { toast } from "sonner";
import { ArrowRight, ArrowUpRight, User, Target, Calendar, ClipboardCheck, HeartHandshake, Shield, Gift } from "lucide-react";
import Seo from "@/components/Seo";
import TrialCTA from "@/components/TrialCTA";
import { trackFormStart, trackLead } from "@/lib/analytics";

const HERO_IMG = "/gallery/gym-floor.webp";

const WHY = [
  {
    icon: User,
    title: "Fully bespoke, one-to-one.",
    body: "Just you and your coach — no shared attention, no shared plan. Every rep, every progression, every check-in is built around you.",
  },
  {
    icon: Target,
    title: "Built around your goals.",
    body: "Strength, fat loss, post-injury rebuilding, return-to-sport, longevity — we work backwards from where you want to be and design the shortest sensible path there.",
  },
  {
    icon: Calendar,
    title: "Fits a busy calendar.",
    body: "Bookable weekly slots from 06:00 on weekdays. Reschedule with a click when life happens — no wasted sessions.",
  },
  {
    icon: ClipboardCheck,
    title: "Written programme + weekly notes.",
    body: "You leave every session with a plan for the days between. Progressions get updated as you get stronger — never left to guess.",
  },
  {
    icon: HeartHandshake,
    title: "Nutrition & recovery guidance.",
    body: "Optional support on eating, sleep and stress — the boring 80% that outperforms every workout you'll ever do.",
  },
  {
    icon: Shield,
    title: "Post-clinical friendly.",
    body: "We regularly work with people bridging medical rehab and full training — always in coordination with your physio or clinician.",
  },
];

const WHOFOR = [
  "You've never trained before and want it done properly from day one.",
  "You're returning to training after an injury, illness or long time away.",
  "You want a discreet, private setting with focused coaching attention.",
  "You've plateaued with group sessions and need a plan built for you.",
  "You want to prepare for something specific — a hike, a marathon, a milestone birthday.",
];

const PROCESS = [
  {
    n: "01",
    t: "Free consultation.",
    d: "A relaxed 30-minute chat — in person at the club or over the phone. We listen, ask the right questions, and give you honest feedback on what would actually help.",
  },
  {
    n: "02",
    t: "Movement screen & plan.",
    d: "A structured first session to see how you move, spot what needs work, and map out the next 8–12 weeks together.",
  },
  {
    n: "03",
    t: "Weekly 1-1 sessions.",
    d: "Book 1, 2 or 3 sessions a week. Each is 60 minutes, fully coached, and adjusted week-on-week to keep you progressing.",
  },
  {
    n: "04",
    t: "Reviews every 4 weeks.",
    d: "Sit-down check-ins to look at progress, refresh goals and update the plan. No fluff, no filler — just clarity.",
  },
];

const PACKAGES = [
  { name: "Single Session", price: "£60", per: "/session", detail: "One-off session. Perfect for a screen, a technique tune-up or a taster." },
  { name: "Weekly", price: "£220", per: "/month", detail: "One coached 1-1 session every week, plus a written plan for the days between.", highlighted: true },
  { name: "Twice Weekly", price: "£400", per: "/month", detail: "Two coached 1-1 sessions every week — our most popular option for consistent progress." },
];

export default function PersonalTraining() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", goals: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await api.post("/consultation", {
        name: form.name,
        email: form.email,
        phone: form.phone || null,
        goals: form.goals || "1-to-1 Personal Training consultation",
        preferred_time: "programme=1to1-pt",
      });
      setDone(true);
      trackLead({ form_name: "pt_consultation", enquiry_type: "personal_training" });
      toast.success("Received — we'll be in touch within 24 hours.");
    } catch (err) { toast.error(errText(err)); } finally { setBusy(false); }
  };

  return (
    <div data-testid="pt-page">
      <Seo
        title="1-to-1 Personal Training — Bowness-on-Windermere"
        description="Fully bespoke 1-to-1 personal training at Kore Wellbeing, Bowness-on-Windermere. Coached sessions, a written weekly plan, nutrition guidance and monthly reviews. Book a free consultation."
        path="/personal-training"
      />

      {/* HERO */}
      <section className="relative min-h-[86vh] flex items-end overflow-hidden bg-kore-forest">
        <img src={HERO_IMG} alt="Kore Wellbeing training floor" className="absolute inset-0 w-full h-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-b from-kore-forest/50 via-kore-forest/60 to-kore-forest/95" />
        <div className="relative container-kore pb-24 md:pb-32 max-w-4xl">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1 }}>
            <span className="text-[11px] uppercase tracking-[0.28em] text-kore-light block">1-to-1 Personal Training</span>
            <h1 className="mt-6 font-display font-light text-white text-5xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-tight">
              A programme built<br/>
              <span className="italic text-kore-light">entirely around you.</span>
            </h1>
            <p className="mt-8 max-w-xl text-kore-warm/85 text-base sm:text-lg font-light leading-relaxed">
              One coach. One plan. All your attention. Kore's 1-to-1 personal training is for people who want to do this properly — with a private, coached space and a plan that adapts to your body every week.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <a href="#book" data-testid="pt-hero-cta" className="btn-gold">Book a free consultation <ArrowRight size={16} /></a>
              <Link to="/memberships" className="btn-ghost !text-kore-warm !border-kore-warm/30">See memberships</Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* WHY */}
      <section className="container-kore py-24">
        <div className="max-w-2xl mb-14">
          <span className="eyebrow">Why 1-to-1</span>
          <h2 className="display-2 mt-4 text-kore-forest">The most efficient way<br/>to actually change.</h2>
          <p className="mt-6 text-kore-ink/70 leading-relaxed">Small-group coaching is brilliant, and it's how most of Kore works. But some people need — or want — full attention on their session. That's what this is for.</p>
        </div>
        <div className="grid md:grid-cols-2 gap-x-14 gap-y-12">
          {WHY.map((w) => {
            const Icon = w.icon;
            return (
              <div key={w.title} className="flex gap-5">
                <div className="shrink-0 w-11 h-11 rounded-full border border-kore-gold/40 flex items-center justify-center text-kore-forest">
                  <Icon size={18} />
                </div>
                <div>
                  <h3 className="font-display text-2xl text-kore-forest leading-tight">{w.title}</h3>
                  <p className="mt-2 text-kore-ink/75 leading-relaxed text-sm">{w.body}</p>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* PROCESS */}
      <section className="bg-kore-forest text-kore-warm py-24">
        <div className="container-kore grid md:grid-cols-12 gap-14">
          <div className="md:col-span-4">
            <span className="eyebrow text-kore-light">How it works</span>
            <h2 className="display-2 mt-4">Four steps.<br/>Then real progress.</h2>
            <p className="mt-6 text-kore-warm/70 leading-relaxed font-light max-w-sm">Every 1-to-1 client starts with a free, honest consultation. No pitch, no commitment — just a conversation.</p>
          </div>
          <div className="md:col-span-7 md:col-start-6 space-y-8">
            {PROCESS.map((p) => (
              <div key={p.n} className="flex gap-6 border-b border-kore-light/20 pb-6">
                <div className="text-kore-light text-sm font-medium w-10 shrink-0">{p.n}</div>
                <div>
                  <h3 className="font-display text-2xl">{p.t}</h3>
                  <p className="mt-2 text-kore-warm/80 leading-relaxed">{p.d}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHO IT'S FOR */}
      <section className="container-kore py-24">
        <div className="grid md:grid-cols-12 gap-14">
          <div className="md:col-span-4">
            <span className="eyebrow">Who it's for</span>
            <h2 className="display-2 mt-4 text-kore-forest">Adults ready to<br/>be properly coached.</h2>
          </div>
          <div className="md:col-span-7 md:col-start-6">
            <ul className="space-y-4">
              {WHOFOR.map((w, i) => (
                <li key={w} className="flex items-start gap-4 border-b border-kore-gold/20 pb-4">
                  <span className="text-kore-gold text-xs font-medium mt-1.5">0{i + 1}</span>
                  <span className="text-kore-ink/85">{w}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* PACKAGES */}
      <section className="bg-kore-light/30 py-24">
        <div className="container-kore">
          <div className="max-w-2xl mb-14">
            <span className="eyebrow">Packages</span>
            <h2 className="display-2 mt-4 text-kore-forest">Simple, honest pricing.</h2>
            <p className="mt-6 text-kore-ink/70 leading-relaxed">Choose the frequency that fits your life — you can flex up or down month to month. All sessions are 60 minutes and include a written follow-on plan.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {PACKAGES.map((p) => (
              <div
                key={p.name}
                data-testid={`pt-package-${p.name.toLowerCase().replace(/\s+/g, "-")}`}
                className={`rounded-3xl p-8 relative ${p.highlighted ? "bg-kore-forest text-kore-warm border-2 border-kore-light" : "bg-white text-kore-forest border border-kore-gold/20"}`}
              >
                {p.highlighted && <span className="absolute -top-3 left-8 bg-kore-light text-kore-forest text-[11px] tracking-[0.2em] uppercase px-4 py-1 rounded-full">Most Popular</span>}
                <div className={`text-[11px] uppercase tracking-[0.22em] ${p.highlighted ? "text-kore-light" : "text-kore-gold"}`}>{p.name}</div>
                <div className="mt-4 flex items-baseline gap-1">
                  <span className="font-display text-5xl font-light">{p.price}</span>
                  <span className={`text-sm ${p.highlighted ? "text-kore-warm/60" : "text-kore-ink/50"}`}>{p.per}</span>
                </div>
                <p className={`mt-5 text-sm leading-relaxed ${p.highlighted ? "text-kore-warm/80" : "text-kore-ink/75"}`}>{p.detail}</p>
              </div>
            ))}
          </div>
          <p className="mt-10 text-xs text-kore-ink/60 max-w-2xl">Prices for guidance only — the right frequency and package are agreed at your free consultation. Kore Coaching members receive a 15% discount on all 1-to-1 sessions.</p>
        </div>
      </section>

      {/* BOOKING */}
      <section id="book" className="container-kore py-24 grid md:grid-cols-12 gap-12">
        <div className="md:col-span-5">
          <span className="eyebrow">Book</span>
          <h2 className="display-2 mt-4 text-kore-forest">Free consultation.<br/>Zero pressure.</h2>
          <p className="mt-6 text-kore-ink/75 leading-relaxed">
            Leave your details and we'll get back to you within 24 hours to arrange a relaxed, 30-minute chat — in person at Kore or over the phone. Bring your questions. Leave with a clear next step.
          </p>
          <div className="mt-8 space-y-3 text-sm text-kore-ink/70">
            <div className="flex items-center gap-3"><span className="w-1.5 h-1.5 rounded-full bg-kore-gold" /> Free and no obligation</div>
            <div className="flex items-center gap-3"><span className="w-1.5 h-1.5 rounded-full bg-kore-gold" /> Honest feedback, even if we're not the right fit</div>
            <div className="flex items-center gap-3"><span className="w-1.5 h-1.5 rounded-full bg-kore-gold" /> Reply within one working day</div>
          </div>
          <div className="mt-10 text-sm text-kore-ink/60">
            Prefer to talk first? <a href="mailto:info@korewellbeing.com" className="text-kore-forest underline decoration-kore-gold underline-offset-4">Email us</a> or find us at Rowan House, Bowness-on-Windermere.
          </div>
        </div>

        <div className="md:col-span-6 md:col-start-7">
          <div className="bg-white rounded-3xl p-10 shadow-soft border border-kore-gold/10">
            {!done ? (
              <form onSubmit={submit} onFocus={() => trackFormStart({ form_name: "pt_consultation" })} data-testid="pt-consultation-form" className="space-y-5">
                <div>
                  <label className="eyebrow">Your name</label>
                  <input required data-testid="pt-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-2 w-full border-b border-kore-ink/20 py-2 focus:outline-none focus:border-kore-gold bg-transparent" />
                </div>
                <div>
                  <label className="eyebrow">Email</label>
                  <input required type="email" data-testid="pt-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-2 w-full border-b border-kore-ink/20 py-2 focus:outline-none focus:border-kore-gold bg-transparent" />
                </div>
                <div>
                  <label className="eyebrow">Phone (optional)</label>
                  <input data-testid="pt-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-2 w-full border-b border-kore-ink/20 py-2 focus:outline-none focus:border-kore-gold bg-transparent" />
                </div>
                <div>
                  <label className="eyebrow">What are you hoping to work on?</label>
                  <textarea rows={4} data-testid="pt-goals" value={form.goals} onChange={(e) => setForm({ ...form, goals: e.target.value })} placeholder="A quick sentence or two — strength, fat loss, post-injury, longevity, anything really." className="mt-2 w-full border-b border-kore-ink/20 py-2 focus:outline-none focus:border-kore-gold bg-transparent resize-none" />
                </div>
                <button disabled={busy} data-testid="pt-submit" className="btn-primary w-full mt-4">
                  {busy ? "Sending…" : (<>Request my free consultation <ArrowRight size={14} /></>)}
                </button>
                <p className="text-[11px] text-kore-ink/50 leading-relaxed mt-3">One personal reply from our team within 24 hours. No mailing lists you didn't ask for.</p>
              </form>
            ) : (
              <div data-testid="pt-success" className="text-center py-8">
                <div className="w-14 h-14 mx-auto rounded-full bg-kore-light flex items-center justify-center text-kore-forest">
                  <HeartHandshake size={22} />
                </div>
                <h3 className="font-display text-3xl mt-6 text-kore-forest">Thanks — we've got it.</h3>
                <p className="mt-3 text-kore-ink/70">We'll be in touch within 24 hours to arrange your free consultation.</p>
                <div className="mt-8 flex gap-3 justify-center flex-wrap">
                  <Link to="/memberships/kore-coached" className="btn-ghost">See Kore Coaching</Link>
                  <Link to="/scorecard" className="btn-ghost">Take the scorecard <ArrowUpRight size={12} /></Link>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      <TrialCTA variant="inline" />
    </div>
  );
}
